import { ChangeEvent, KeyboardEvent, SyntheticEvent, useRef, useState } from 'react';
import { useConditionalEffect, usePrevious } from 'react-delta';
import classNames from 'classnames';
import BootstrapForm from 'react-bootstrap/Form';
import ClearIcon from '@mui/icons-material/Cancel';

import * as K from 'common/constants';
import { DynamicTestId } from 'common/element';
import { getCursorPosition, setCursorPosition } from 'service/browser';

import './ClearableInput.scss';

type ClearableInputProps = {
  value?: string;
  placeholder?: string;
  className?: string;
  onChange: (value: string) => void;
  onEnter?: (value: string) => void;
  onKeyDown?: (event: KeyboardEvent) => void;
  onClear?: (event: SyntheticEvent) => void;
  onFocus?: (event: SyntheticEvent) => void;
  onBlur?: (value: string) => void;
  required?: boolean;
  clearable?: boolean;
  size?: 'sm' | 'lg';
  rightIcon?: JSX.Element;
  disabled?: boolean;
  testId?: DynamicTestId;
  invalidError?: string;
  autoFocus?: boolean;
};

/**
 * This component is a text input that has an optional button for clearing content. This is a controlled component,
 * so it requires the onChange property to maintain the state of its value.
 *
 * @prop value          text content of the input
 * @prop placeholder    text to show when the input is empty
 * @prop className      additional class name(s) for the input
 * @prop onChange       function called with new value of text input
 * @prop onEnter        function to call when the user presses the Enter key
 * @prop onKeyDown      function to call when the user presses a key
 * @prop onClear        function to call when the user has cleared the input
 * @prop onBlur         function to call when the input loses focus
 * @prop required       if true, the input is required. Useful for validation.
 * @prop clearable      if true (the default), show a clear button
 * @prop size           size of input
 * @prop rightIcon      icon to show on right side
 * @prop disabled       if true, disable the input
 * @prop testId         uniquely identifying string for the input
 * @prop invalidError   BootstrapForm.Control.Feedback message to display to the user when input is not valid
 * @prop autoFocus      native React autoFocus prop
 */
function ClearableInput({
  value = '',
  placeholder,
  className,
  onChange,
  onEnter,
  onKeyDown,
  onClear,
  onFocus,
  onBlur,
  required,
  clearable = true,
  size,
  rightIcon,
  disabled = false,
  testId,
  invalidError = 'Required',
  autoFocus = false,
}: ClearableInputProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [cursorPos, setCursorPos] = useState(-1);

  const prevValue = usePrevious(value);
  useConditionalEffect(() => {
    const input = inputRef?.current;
    if (input) {
      // restore cursor position; -1 means end of input
      setCursorPosition(input, cursorPos);
    }
  }, !!prevValue && !!value && value !== prevValue); // check for values so we don't run when going from undefined to ''

  // handle content changes
  const handleChange = (event: ChangeEvent): void => {
    const element = event.target as HTMLInputElement;
    // remember cursor position in case element gets re-rendered
    let cursor = getCursorPosition(element);
    if (cursor === element.value.length) {
      cursor = -1;
    }
    setCursorPos(cursor);
    onChange(element.value || '');
  };

  // handle Enter key
  const handleKeyDown = (event: KeyboardEvent): void => {
    if (onEnter) {
      const key = event.keyCode;
      if (key === K.KEY_CODE.ENTER) {
        onEnter(value);
        event.preventDefault();
      }
    }
    if (onKeyDown) {
      onKeyDown(event);
    }
  };

  // handle clear icon click
  const handleClear = (event: SyntheticEvent): void => {
    onChange('');
    // set focus on input after clear
    if (inputRef.current) {
      inputRef.current.focus();
    }
    if (onClear) {
      onClear(event);
    }
    event.preventDefault();
    event.stopPropagation(); // eat the click event
  };

  // call blur handler if provided
  const handleBlur = (): void => {
    if (onBlur) {
      onBlur(value);
    }
  };

  let rightContent = null;
  const rightIconClass = classNames('ClearableInput__RightIcon', 'ClearableInput__ClearIcon', {
    'ClearableInput__RightIcon--Small': size === 'sm',
    'ClearableInput__RightIcon--Large': size === 'lg',
  });
  if (clearable && !!value) {
    let fontSize: 'small' | 'large' | undefined;
    if (size) {
      fontSize = size === 'lg' ? 'large' : 'small';
    }
    const closeButtonTestId = testId ? testId + '-close' : undefined;
    rightContent = (
      <div className={rightIconClass} data-test-id={closeButtonTestId}>
        <ClearIcon fontSize={fontSize} onClick={(ev: SyntheticEvent): void => handleClear(ev)} />
      </div>
    );
  } else if (rightIcon) {
    rightContent = <div className={rightIconClass}>{rightIcon}</div>;
  }

  return (
    <div className="ClearableInput__Container">
      <BootstrapForm.Control
        ref={inputRef}
        className={classNames('ClearableInput', className)}
        as="input"
        type="text"
        autoComplete="off"
        spellCheck="false"
        size={size}
        value={value}
        placeholder={placeholder}
        onChange={(ev: ChangeEvent): void => handleChange(ev)}
        onKeyDown={(ev: KeyboardEvent): void => handleKeyDown(ev)}
        onBlur={(): void => handleBlur()}
        required={required}
        disabled={disabled}
        data-test-id={testId}
        autoFocus={autoFocus}
        onFocus={onFocus}
      />
      {rightContent}
      <BootstrapForm.Control.Feedback type="invalid">{invalidError}</BootstrapForm.Control.Feedback>
    </div>
  );
}

export default ClearableInput;
